import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  Renderer2
} from '@angular/core';
import {ItemManagerService} from "../../services/itemManager/item-manager.service";
import {debounceTime} from "rxjs/operators";
import {animateShow} from "../../animations/showHideAnimation";
import {ObjectConfig} from "../../models/ObjectConfig";
import {Item} from "../../models/Item";
import {ObjectsArrayService} from "../../services/objectsArrayService/objects-array.service";
import {GlobalEventsService} from "../../services/global-events.service";
import {Subject, timer, Subscription} from 'rxjs';
import {isViewMode, isViewModeByShareLink} from '@src/configs/view_only';
import {CesiumAdapterService} from '@src/app/services/cesiumAdapter21/cesium-adapter.service';
import {UiAdapterService} from '@src/app/services/uiAdapterService/ui-adapter.service';
import {ObjectArrayPlugin} from '@src/app/classes/plugins/objectArrayPlugin/object-array-plugin';
import {GroupService} from "../../services/groupService/group-service.service";
declare var Yiit;

@Component({
  selector: 'app-selected-items-group',
  templateUrl: './selected-items-group.component.html',
  styleUrls: ['./selected-items-group.component.scss'],
  animations: [animateShow],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectedItemsGroupComponent implements OnInit, OnDestroy{

  private groupListSubscription: Subscription;
  public Yiit = Yiit;
  public isOpened = false;
  public selectedItems:{[key: string]:Item} = {};
  public selectedGroups = {};
  public openedGroup = null;
  public isViewOnly = isViewMode || isViewModeByShareLink;
  private listObserver: MutationObserver = null;
  private isObservingListInitialized: boolean = null;
  private listRendered$:Subject<boolean> = new Subject<boolean>();

  @ViewChild('content', {static: false}) contentElement: ElementRef;

  constructor(
      private itemManager: ItemManagerService,
      private cd: ChangeDetectorRef,
      private gs: GroupService,
      private eventService: GlobalEventsService,
      private ca: CesiumAdapterService,
      private ui: UiAdapterService,
      private objectsArray: ObjectsArrayService,
      private renderer: Renderer2
  ) { }

  ngOnInit() {
    this.itemManager.selectedItemList$
        .pipe(debounceTime(100))
        .subscribe((list:{[key:string]: Item}) => {
          this.openedGroup = null;
          this.selectedItems = list;
          this.updateSelectedGroups();
          if (Object.keys(list).length == 0){
            this.eventService.ToastNotificationEvent.emit({message: ''});
            this.eventService.isSomeHintVisible = false;
          }
          this.cd.markForCheck();

          if (this.isOpened) {
            setTimeout(() => this.setScrollToElement(), 0);
          }
        });

    this.ca.onEmptyClick$.subscribe(()=>{
      this.clearMenu();
      this.ui.selectedItem$ = null;
    })

    this.groupListSubscription = this.gs.groupList$.subscribe(() => {
      this.updateSelectedGroups();
    });
  }

  ngOnDestroy() {
    if (this.listObserver) {
      this.listObserver.disconnect();
    }

  if (this.groupListSubscription) {
    this.groupListSubscription.unsubscribe();
  }
}


  getItemImg(item){
    // @ts-ignore
    return item.config.getObjectConfig().getMapIcon() || item.config.getTool().menu_icon || null;
  }

  private getFixedFiguresFromSelectedItems(){
    let selectedModelItems = {};
    for (let i in this.selectedItems){
      let _item:Item = this.selectedItems[i];
      if (_item.config.getTool().type == ObjectConfig.TYPE_FIXED_FIGURE){
        selectedModelItems[i] = _item;
      }
    }
    return selectedModelItems;
  }

  isCanGroupItems(){
    let isItemsContainArray = false;
    let parentItem:Item = null;
    let selectedModelItems = this.getFixedFiguresFromSelectedItems();


    for (let i in selectedModelItems){
      let _item:Item = this.selectedItems[i];

      if (_item.config.isCopy || ObjectArrayPlugin.isItemHasObjectArray(_item)){
        isItemsContainArray = true;
        parentItem = (_item.config.isCopy) ? this.itemManager.getItemById(_item.config.getCopyParentId): _item;
        break;
      }
    }
    if (isItemsContainArray){
      let tmpSelectedItemsObj = Object.assign({}, selectedModelItems);
      delete tmpSelectedItemsObj[parentItem.getId()];

      let isAllItemsFromOneGroup = true;
      for (let i in tmpSelectedItemsObj) {
        let _item: Item = tmpSelectedItemsObj[i];
        if (_item.config.getCopyParentId != parentItem.config.getId()){
          isAllItemsFromOneGroup = false;
          break;
        }
      }
      return !isAllItemsFromOneGroup;
    }

    return Object.keys(selectedModelItems).length > 1;
  }

  isCanUngroupItems(){
    let selectedModelItems = this.getFixedFiguresFromSelectedItems();

    for (let i in selectedModelItems){
      let _item:Item = selectedModelItems[i];
      if (_item.config.getInputProp('__objectArray', 'value') || _item.config.isCopy){
        return true;
      }
    }
    return false;
  }

  /**
   * return items that are parent of objects array
   */
  getGroupsParentItems(){
    let resultArr = [];
    let selectedModelItems =this.getFixedFiguresFromSelectedItems();
    for (let i in selectedModelItems){
      let _item = selectedModelItems[i]
      if (!_item.config.isCopy && !_item.config.getInputProp('__objectArray', 'value')){
        continue;
      }
      let parentId = _item.config.isCopy ? _item.config.isCopy : _item.getId();
      if (resultArr.indexOf(parentId) < 0){
        resultArr.push(parentId);
      }
    }
    return resultArr;
  }

  groupItems(event){
    event.stopImmediatePropagation();
    event.stopPropagation();
    this.objectsArray.groupItems(this.selectedItems);
  }

  ungroupItems(event){
    event.stopImmediatePropagation();
    event.stopPropagation();
    if (
      confirm(Yiit.t('This operation converts every array item to an individual map object. Would you like to continue?'))
    ) {
      let itemsId = this.getGroupsParentItems();
      itemsId.forEach((id) => {
        this.itemManager.getItemById(id).getObservable('ungroup').next(true);
      });
      timer(500).subscribe(() => {
        this.itemManager.updateSelectedItemsGroup();
      });
    }
  }

  clearMenu(){
    this.selectedItems = {};
    this.itemManager.clearSelectedItems();
    this.itemManager.duplicateCallCounts = {};
    this.cd.detectChanges();
  }

  getSelectedItemsList(){
    return this.selectedItems;
  }

  updateSelectedGroups() {
    this.selectedGroups = {};
    for (let i in this.selectedItems) {
      let item:Item = this.selectedItems[i];
      let groupId = item.config.getGroupId();
      let groupName = 'No Category';

      if (groupId) {
        let group = this.gs.getGroupById(groupId);
        if (group) {
          groupName = group.getTitle();
        }
      }

      this.addItemToGroup(item, groupId || 'No Category', groupName);
    }
    this.cd.detectChanges();
  }

  private addItemToGroup(item: Item, groupId: string, groupName: string) {
    if (!this.selectedGroups.hasOwnProperty(groupId)) {
      this.selectedGroups[groupId] = {
        name: groupName,
        items: []
      };
    }
    this.selectedGroups[groupId].items.push(item);
  }

  getGroupIds() {
    return Object.keys(this.selectedGroups);
  }

  isVisible() {
    return this.getCount() > 0;
  }

  getCount(){
    return Object.keys(this.getSelectedItemsList()).length
  }

  toggleOpen(){
    if (this.isOpened) {
      this.removeScroll();
    }
    this.isOpened = !this.isOpened;
  }

  private setScrollToElement() {
    if (this.contentElement && this.contentElement.nativeElement) {
      const contentEl = this.contentElement.nativeElement;
      if (contentEl.scrollHeight > 250) {
        this.renderer.setStyle(contentEl, 'overflowY', 'auto');
      } else {
        this.renderer.setStyle(contentEl, 'overflowY', 'hidden');
      }
    }
  }

  private removeScroll() {
    if (this.contentElement && this.contentElement.nativeElement) {
      this.renderer.setStyle(this.contentElement.nativeElement, 'overflowY', 'hidden');
    }
  }


  openingAnimationDone($event) {
    if (this.isOpened && $event.toState === 'open') {
      this.setScrollToElement();
    }
  }

  removeItems(e, groupItems?){
    e.stopPropagation();
    e.stopImmediatePropagation();
    if (confirm(this.Yiit.t('Are you sure?'))){
      this.itemManager.removeSelectedItems(groupItems);
    }
  }

  isGroupContainsFixedFigure(groupItems){
    let list = groupItems.filter((item:Item)=>{return item.config.getTool().type == ObjectConfig.TYPE_FIXED_FIGURE});
    return list.length;
  }

  toggleExchangeItemOpenStatus(groupName){
    if (this.openedGroup == groupName){
      this.openedGroup = null;
    }else{
      this.openedGroup = groupName;
    }
  }

  isExchangeItemOpened(groupName){
    return this.openedGroup == groupName;
  }

    private duplicateItem(item: Item) {
      const itemId = item.getId();

      if (!this.itemManager.duplicateCallCounts[itemId]) {
          this.itemManager.duplicateCallCounts[itemId] = 0;
      }

      this.itemManager.duplicateCallCounts[itemId]++;
      const duplicateCallCount = this.itemManager.duplicateCallCounts[itemId];

      const isParentItem = ObjectArrayPlugin.isItemHasObjectArray(item);
      this.itemManager.duplicateItem(item, undefined, undefined, duplicateCallCount).then((newItem: Item) => {
          if (isParentItem) {
              const copies = this.itemManager.getAllItemCopies(item);
              copies.forEach((copy: Item) => {
                  this.itemManager.duplicateItem(copy, undefined, { copyOf: newItem.getId() }, duplicateCallCount);
              });
          }
      }).catch((error) => {
          console.error('Duplication failed', error);
      });
  }

  duplicateGroup(group: { name: string; items: Item[] }) {
    group.items.forEach((item) => {
      this.duplicateItem(item);
    });
  }

  duplicateAllItems(event: Event) {
    event.stopImmediatePropagation();
    for (let groupId in this.selectedGroups) {
      let group = this.selectedGroups[groupId];
      group.items.forEach((item) => {
        this.duplicateItem(item);
      });
    }
  }
}
